/**
 * Feature flags as the backend reports them.
 *
 * A flag is two things at once: a `feature_flag` row carrying the global
 * switches, and a per-organization `org_feature` row for each practice let in
 * individually. Both surfaces below reflect that split.
 */

/** One flag and its global switches, from GET /api/v1/feature-flags. */
export interface FeatureFlag {
  name: string
  description: string
  /** Whether the flag is live as a global concept. */
  enabled: boolean
  /** Whether newly created organizations are granted it. */
  enable_new_orgs: boolean
  /** Whether it reaches every organization holding no row of its own. */
  enable_all_orgs: boolean
}

/**
 * Body accepted by PUT /api/v1/feature-flags/{name}.
 *
 * Every field is required: the endpoint replaces the whole set of switches
 * rather than patching the ones it was given.
 *
 * `enabled` is absent on purpose — the endpoint forbids it. Withdrawing a flag
 * as a concept is a deliberate database change rather than a rollout step. It is
 * still reported on `FeatureFlag`, so a flag that is not live can be shown.
 */
export interface UpdateFeatureFlagRequest {
  enable_new_orgs: boolean
  enable_all_orgs: boolean
  description: string
}

/** How one flag currently reaches one organization. */
export type OrgFeatureState =
  | 'ACTIVE'
  | 'ALL_ORGS'
  | 'SCHEDULED'
  | 'EXPIRED'
  | 'DISABLED'
  | 'DISABLED_WINDOWED'
  | 'NOT_SET'

/**
 * What each state is called, whether the practice has the feature, and whether a
 * switch here can move it.
 *
 * A `Record` over the union rather than three lists of states, so a state added
 * to `OrgFeatureState` has to answer all three questions instead of falling
 * through to the permissive answer — a live switch over a state the backend will
 * refuse to move.
 *
 * `ALL_ORGS` reads simply as on: it is on for this practice, and it is unmovable
 * because the flag grants it rather than this practice, so a per-organization row
 * can neither add to it nor take it away. A windowed row was written deliberately
 * by someone, and clearing that window is left to a person.
 */
interface OrgFeatureStateDisplay {
  label: string
  isOn: boolean
  isMovable: boolean
}

export const ORG_FEATURE_STATE_DISPLAY: Record<OrgFeatureState, OrgFeatureStateDisplay> = {
  ACTIVE: { label: 'On', isOn: true, isMovable: true },
  ALL_ORGS: { label: 'On', isOn: true, isMovable: false },
  SCHEDULED: { label: 'Starts later', isOn: false, isMovable: false },
  EXPIRED: { label: 'Window closed', isOn: false, isMovable: false },
  DISABLED: { label: 'Off', isOn: false, isMovable: true },
  // Reads as off rather than as a window, because that is the part an operator
  // acts on: the row is switched off, and it would stay off if the window opened.
  DISABLED_WINDOWED: { label: 'Off', isOn: false, isMovable: false },
  NOT_SET: { label: 'Off', isOn: false, isMovable: true },
}

/**
 * How to show a state, tolerating one this bundle has not heard of.
 *
 * The `Record` is exhaustive over the union, but the value arrives over the
 * network: during a rolling deploy a newer backend can name a state this bundle
 * predates. Falling back to off-and-unmovable keeps the tab rendering and offers
 * no switch over a state whose rules are unknown, rather than throwing on the
 * lookup and taking the page down.
 */
const orgFeatureStateDisplay = (state: OrgFeatureState): OrgFeatureStateDisplay =>
  ORG_FEATURE_STATE_DISPLAY[state] ?? { label: 'Unknown', isOn: false, isMovable: false }

export const getOrgFeatureStateLabel = (state: OrgFeatureState): string =>
  orgFeatureStateDisplay(state).label

export const isOrgFeatureOn = (state: OrgFeatureState): boolean =>
  orgFeatureStateDisplay(state).isOn

export const isOrgFeatureMovable = (state: OrgFeatureState): boolean =>
  orgFeatureStateDisplay(state).isMovable

/**
 * Why a state offers no switch, where that needs saying.
 *
 * `ALL_ORGS` is deliberately absent: it is replaced by a "Globally Enabled"
 * message rather than a disabled switch, and that message is the explanation.
 */
export const UNMOVABLE_ORG_FEATURE_REASON: Partial<Record<OrgFeatureState, string>> = {
  SCHEDULED:
    'This practice has a row that starts later. Changing that window is a database change.',
  EXPIRED:
    'This practice has a row whose window has closed. Changing that window is a database change.',
  DISABLED_WINDOWED:
    'This practice has a row that is switched off and carries a window that is not ' +
    'open. Clearing both is a database change.',
}

/** One flag as it reaches one organization. */
export interface OrgFeatureFlag {
  name: string
  description: string
  state: OrgFeatureState
}

/**
 * Body accepted by POST /api/v1/feature-flags/organizations/{org_id}.
 *
 * The changes rather than the whole set, so a flag nobody touched cannot be
 * rewritten by a stale page. A name in both lists is rejected.
 */
export interface SetOrgFeatureFlagsRequest {
  enable: string[]
  disable: string[]
}

/** What happened to one organization in a rollout, in either direction. */
export type RolloutOutcome =
  | 'ENABLED'
  | 'ALREADY_ENABLED'
  | 'DISABLED'
  | 'ALREADY_DISABLED'
  | 'ALL_ORGS'
  | 'BLOCKED'
  | 'NOT_FOUND'
  | 'FAILED'

/**
 * How each outcome reads, and how loudly.
 *
 * The `ALREADY_*` pair is deliberately not a success colour: re-running a list is
 * normal and expected, so it should not look like the rollout did work it did
 * not do.
 */
interface RolloutOutcomeDisplay {
  label: string
  severity: 'success' | 'info' | 'warning' | 'error'
}

export const ROLLOUT_OUTCOME_DISPLAY: Record<RolloutOutcome, RolloutOutcomeDisplay> = {
  ENABLED: { label: 'Turned on', severity: 'success' },
  ALREADY_ENABLED: { label: 'Already on', severity: 'info' },
  DISABLED: { label: 'Turned off', severity: 'success' },
  ALREADY_DISABLED: { label: 'Already off', severity: 'info' },
  // No response carries this today — both write surfaces refuse an all-orgs flag
  // outright rather than reporting it per organization. Kept because the outcome
  // is part of the API's enum, so a future surface could start returning it.
  ALL_ORGS: { label: 'On for all orgs', severity: 'warning' },
  BLOCKED: { label: 'Needs a manual change', severity: 'warning' },
  NOT_FOUND: { label: 'No such organization', severity: 'error' },
  FAILED: { label: 'Failed', severity: 'error' },
}

/** As `orgFeatureStateDisplay`, for the same rolling-deploy reason. */
export const rolloutOutcomeDisplay = (outcome: RolloutOutcome): RolloutOutcomeDisplay =>
  ROLLOUT_OUTCOME_DISPLAY[outcome] ?? { label: outcome, severity: 'warning' }

/** One organization's outcome in a rollout. */
export interface RolloutResult {
  org_id: string
  /** Null when no organization matched the id. */
  display_name: string | null
  outcome: RolloutOutcome
  /** Why the organization was not granted the flag, when it was not. */
  detail: string | null
}

/** Body accepted by POST /api/v1/feature-flags/{name}/rollout. */
export interface RolloutRequest {
  org_ids: string[]
  /** True grants the flag to each organization, false switches it back off. */
  enable: boolean
}

export interface RolloutResponse {
  results: RolloutResult[]
}

/** Matches the backend's own cap on one rollout request. */
export const MAX_ROLLOUT_ORGS = 50

/** Matches the backend's own cap on a flag description. */
export const MAX_FLAG_DESCRIPTION_LENGTH = 500
